import type {
	GridCustomCellProps,
	GridItemChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { useMemo } from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { RoutingGuideEnumsType } from "~/shared/schemas/routing-guide-schema.ts";

import { ConditionCell } from "../../grid/cells/condition-cell/index.tsx";
import { FieldSelectorCell } from "../../grid/cells/field-selector-cell/index.tsx";
import { FieldTextCell } from "../../grid/cells/field-text-cell/index.tsx";
import { OperatorCell } from "../../grid/cells/operator-cell/index.tsx";
import { RuleActionsCell } from "../../grid/cells/rule-actions-cell/index.tsx";
import type { RowUpdate, Rule } from "../utils/types.ts";

export const useRuleColumns = (
	handleRuleRowChange: (event: GridItemChangeEvent | RowUpdate<Rule>) => void,
	handleRemoveRow: (id: number) => void,
	showActivationErrors = false,
	routingGuideEnums?: RoutingGuideEnumsType,
) => {
	const { isInternal } = useDeploymentType();

	const fieldColumns = useMemo(
		() =>
			Array.from({ length: 6 }, (_, i) => ({
				field: `field${i + 1}`,
				title: `Field ${i + 1}`,
				menu: false,
				show: true,
				sortable: false,
				cells: {
					data: (props: GridCustomCellProps) => {
						return (
							<FieldTextCell
								cellTestId={`routing-guide-condition-cell-field-${i + 1}`}
								fieldNum={i}
								handleRowChange={handleRuleRowChange}
								isReadOnly={!isInternal}
								routingGuideEnums={routingGuideEnums}
								showActivationError={showActivationErrors}
								{...props}
							/>
						);
					},
				},
			})),
		[handleRuleRowChange, isInternal, routingGuideEnums, showActivationErrors],
	);

	return useMemo(
		() => [
			{
				field: "condition",
				title: "Condition",
				menu: false,
				show: true,
				sortable: false,
				cells: {
					data: (props: GridCustomCellProps) => (
						<ConditionCell
							cellTestId={"routing-guide-condition-cell-condition"}
							handleRuleRowChange={handleRuleRowChange}
							isReadOnly={!isInternal}
							routingGuideEnums={routingGuideEnums}
							{...props}
						/>
					),
				},
			},
			{
				field: "fieldSelector",
				title: "Field Selector",
				menu: false,
				sortable: false,
				show: true,
				cells: {
					data: (props: GridCustomCellProps) => (
						<FieldSelectorCell
							cellTestId={"routing-guide-condition-cell-field-selector"}
							handleRuleRowChange={handleRuleRowChange}
							isReadOnly={!isInternal}
							routingGuideEnums={routingGuideEnums}
							showActivationError={showActivationErrors}
							{...props}
						/>
					),
				},
			},
			{
				field: "operator",
				title: "Operator",
				menu: false,
				show: true,
				sortable: false,
				cells: {
					data: (props: GridCustomCellProps) => (
						<OperatorCell
							cellTestId={"routing-guide-condition-cell-operator"}
							handleRowChange={handleRuleRowChange}
							isReadOnly={!isInternal}
							routingGuideEnums={routingGuideEnums}
							showActivationError={showActivationErrors}
							{...props}
						/>
					),
				},
			},
			...fieldColumns,
			...(isInternal
				? [
						{
							field: "",
							title: "Actions",
							menu: false,
							show: true,
							sortable: false,
							cells: {
								data: (props: GridCustomCellProps) => (
									<RuleActionsCell
										cellTestId={"routing-guide-condition-cell-actions"}
										handleRemoveRow={handleRemoveRow}
										{...props}
									/>
								),
							},
						},
					]
				: []),
		],
		[
			fieldColumns,
			handleRuleRowChange,
			isInternal,
			handleRemoveRow,
			routingGuideEnums,
			showActivationErrors,
		],
	);
};
